import { FC } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from 'recharts';
import type { VisaoGeralMaquinaLinha, VisaoGeralMes } from '@/types/dashboards';
import { produzidoBase } from '@/utils/equiplexSop';
import { fmtCompacto, fmtData, fmtDesvio, fmtUn } from '@/utils/painelFormat';
import { ALTURA_BARRA_HORIZONTAL, tickEixo, type CoresPainel } from '../chartTheme';
import Legenda from '../Legenda';
import PainelCard from '../PainelCard';
import { rotuloNaPonta } from '../rotuloBarra';
import TooltipBox from '../TooltipBox';

interface Props {
  linhas: VisaoGeralMaquinaLinha[];
  mes: VisaoGeralMes;
  /** Último dia fechado: no mês aberto as duas barras só têm as OPs até aqui. */
  fechadoAte: string;
  cores: CoresPainel;
}

type Ponto = VisaoGeralMaquinaLinha & { produzidoComparavel: number };

/**
 * Programado × produzido por máquina no mês escolhido. No mês aberto as duas barras
 * são das OPs que começaram até o último dia fechado — é isso que o desvio compara.
 * O valor de cada barra fica na ponta; o desvio % fica no tooltip e na matriz logo abaixo.
 */
const MaquinasChart: FC<Props> = ({ linhas, mes, fechadoAte, cores }) => {
  const dados: Ponto[] = linhas.map((l) => ({ ...l, produzidoComparavel: produzidoBase(l) }));
  const ate = mes.em_andamento ? ` até ${fmtData(fechadoAte).slice(0, 5)}` : '';
  const conteudoTooltip = ({ active, payload }: TooltipContentProps) => {
    const l = active && payload?.[0] ? (payload[0].payload as Ponto) : null;
    if (!l) return null;
    const itens = [
      { rotulo: `Programado${ate}`, valor: fmtUn(l.base), cor: cores.programado },
      { rotulo: `Produzido${ate}`, valor: fmtUn(l.produzidoComparavel), cor: cores.produzido },
      { rotulo: 'Desvio', valor: fmtDesvio(l.desvio_pct) },
      { rotulo: 'OPs · lotes', valor: `${fmtUn(l.ops)} · ${fmtUn(l.lotes)}` },
    ];
    if (mes.em_andamento) {
      itens.push(
        { rotulo: 'Programado no mês', valor: fmtUn(l.programado) },
        { rotulo: 'Produzido até agora', valor: fmtUn(l.produzido) },
      );
    }
    return <TooltipBox titulo={l.maquina} linhas={itens} />;
  };

  return (
    <PainelCard
      testId="vg-chart-maquinas"
      titulo="Programado × produzido por máquina"
      subtitulo={
        mes.em_andamento
          ? `${mes.label}: OPs que começaram até ${fmtData(fechadoAte)} (dias fechados) e o que foi reportado nelas.`
          : `${mes.label}: OPs do mês e o que foi reportado nelas.`
      }
    >
      <Legenda
        itens={[
          { rotulo: 'Programado (OPs)', cor: cores.programado },
          { rotulo: 'Produzido (reportado nas OPs)', cor: cores.produzido },
        ]}
      />
      {linhas.length === 0 ? (
        <div className="py-10 text-center text-sm text-content-muted">Nenhuma OP neste mês.</div>
      ) : (
        <div style={{ width: '100%', height: Math.max(200, linhas.length * ALTURA_BARRA_HORIZONTAL * 2 + 24) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dados} layout="vertical" margin={{ top: 0, right: 72, bottom: 0, left: 8 }} barGap={3} barCategoryGap={12}>
              <CartesianGrid horizontal={false} stroke={cores.grade} />
              <XAxis type="number" tickFormatter={fmtCompacto} tick={tickEixo(cores)} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="maquina" width={120} tick={tickEixo(cores)} axisLine={false} tickLine={false} />
              <Tooltip content={conteudoTooltip} cursor={{ fill: cores.grade, fillOpacity: 0.35 }} />
              <Bar dataKey="base" name="Programado" fill={cores.programado} radius={[0, 4, 4, 0]} maxBarSize={24} isAnimationActive={false}>
                <LabelList dataKey="base" content={rotuloNaPonta(fmtCompacto, cores.texto)} />
              </Bar>
              <Bar
                dataKey="produzidoComparavel"
                name="Produzido"
                fill={cores.produzido}
                radius={[0, 4, 4, 0]}
                maxBarSize={24}
                isAnimationActive={false}
              >
                <LabelList dataKey="produzidoComparavel" content={rotuloNaPonta(fmtCompacto, cores.texto)} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </PainelCard>
  );
};

export default MaquinasChart;
